import { useCallback, useMemo, useState } from "react";
import { Modal, Button, Group, Divider } from "@mantine/core";
import { PortalSubjectType } from "../../../services/admin/portalAccess.service";
import { usePhysiciansAdmin } from "../../../hooks/admin/usePhysicians";
import { usePractices } from "../../../hooks/admin/usePractices";
import { useSurgeryCentersLabsAdmin } from "../../../hooks/admin/useSurgeryCentersLabs";
import { SubjectGrantForm } from "./SubjectGrantForm";
import { SubjectSelectFields } from "./SubjectSelectFields";
import { MODAL_WIDTH } from "./portalGrantLayout";

interface AddPortalGrantWithSubjectModalProps {
  opened: boolean;
  onClose: () => void;
}

export function AddPortalGrantWithSubjectModal({
  opened,
  onClose,
}: AddPortalGrantWithSubjectModalProps) {
  const [subjectType, setSubjectType] = useState<PortalSubjectType | "">("");
  const [subjectId, setSubjectId] = useState<string>("");

  const { data: physicians } = usePhysiciansAdmin();
  const { data: practices } = usePractices();
  const { data: surgeryCenters } = useSurgeryCentersLabsAdmin();

  const subjectOptions = useMemo(() => {
    if (subjectType === "physician") {
      return (physicians ?? [])
        .map((p) => ({
          value: p.id,
          label: `${p.lastName}, ${p.firstName}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    if (subjectType === "practice") {
      return (practices ?? [])
        .map((p) => ({ value: p.id, label: p.name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    if (subjectType === "surgery_center") {
      return (surgeryCenters ?? [])
        .map((s) => ({ value: s.id, label: s.name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
  }, [subjectType, physicians, practices, surgeryCenters]);

  const handleClose = useCallback(() => {
    setSubjectType("");
    setSubjectId("");
    onClose();
  }, [onClose]);

  const handleSubjectTypeChange = useCallback((value: string | null) => {
    setSubjectType((value as PortalSubjectType | null) ?? "");
    setSubjectId("");
  }, []);

  const handleSubjectIdChange = useCallback((value: string | null) => {
    setSubjectId(value ?? "");
  }, []);

  const subjectSelectProps = useMemo(
    () => ({
      subjectType,
      subjectId,
      subjectOptions,
      onSubjectTypeChange: handleSubjectTypeChange,
      onSubjectIdChange: handleSubjectIdChange,
    }),
    [
      subjectType,
      subjectId,
      subjectOptions,
      handleSubjectTypeChange,
      handleSubjectIdChange,
    ]
  );

  const formReady = useMemo(
    () => Boolean(subjectType && subjectId),
    [subjectType, subjectId]
  );

  return (
    <Modal.Root
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={false}
      size={MODAL_WIDTH}
      centered
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Add Portal Access Grant</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>

        {formReady && subjectType ? (
          // The grant form owns Modal.Body and the footer so that its submit
          // button can live outside the scroll area while staying in the form.
          <SubjectGrantForm
            key={`${subjectType}::${subjectId}`}
            subjectType={subjectType}
            subjectId={subjectId}
            physicians={physicians}
            practices={practices}
            subjectFields={<SubjectSelectFields {...subjectSelectProps} />}
            onSuccess={handleClose}
            onCancel={handleClose}
          />
        ) : (
          <>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <SubjectSelectFields {...subjectSelectProps} />
            </Modal.Body>

            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={handleClose} type="button">
                Cancel
              </Button>
            </Group>
          </>
        )}
      </Modal.Content>
    </Modal.Root>
  );
}
